import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TimeChart from '@/components/time/TimeChart';
import TimeSummary from '@/components/time/TimeSummary';

interface TimeLog {
  id: number;
  project_id: number;
  project_name: string;
  task_id?: number;
  task_name?: string;
  user_id: number;
  user_name: string;
  description: string;
  start_time: string;
  end_time?: string;
  duration_minutes: number;
  is_billable: boolean;
  hourly_rate?: number;
}

interface Filters {
  dateRange: string;
  user: string;
  project: string;
}

const TimeReports = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>({
    dateRange: 'this_month',
    user: 'all',
    project: 'all',
  });

  const { data: timeLogs = [], isLoading } = useQuery<TimeLog[]>({
    queryKey: ['time-reports', filters],
    queryFn: async () => {
      // Mock data - replace with actual API call
      const mockData: TimeLog[] = [];
      const projects = ['LWM PM System', 'Barcelona E-commerce', 'Fixaro Platform'];
      const users = [
        { id: 1, name: 'Roman Siromskyi', rate: 150 },
        { id: 2, name: 'Bohdan Buryi', rate: 80 },
        { id: 3, name: 'Vlad Dev', rate: 75 },
      ];

      // Generate mock data for the last 30 days
      for (let i = 0; i < 30; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);

        for (let j = 0; j < Math.floor(Math.random() * 3) + 1; j++) {
          const user = users[Math.floor(Math.random() * users.length)];
          const project = projects[Math.floor(Math.random() * projects.length)];
          const duration = Math.floor(Math.random() * 480) + 60; // 1-8 hours

          if (user && project) {
            mockData.push({
              id: mockData.length + 1,
              project_id: projects.indexOf(project) + 1,
              project_name: project,
              user_id: user.id,
              user_name: user.name,
              description: 'Development work',
              start_time: date.toISOString(),
              end_time: new Date(date.getTime() + duration * 60000).toISOString(),
              duration_minutes: duration,
              is_billable: Math.random() > 0.3,
              hourly_rate: user.rate,
            });
          }
        }
      }

      return mockData;
    },
  });

  const filteredLogs = timeLogs.filter((log) => {
    if (filters.user !== 'all' && log.user_id !== Number(filters.user)) {
      return false;
    }
    if (filters.project !== 'all' && log.project_id !== Number(filters.project)) {
      return false;
    }
    return true;
  });

  // Aggregate data by project
  const projectData = filteredLogs.reduce((acc, log) => {
    const project = log.project_name;
    if (!acc[project]) {
      acc[project] = 0;
    }
    acc[project] += log.duration_minutes / 60;
    return acc;
  }, {} as Record<string, number>);

  const projectChartData = Object.entries(projectData).map(([name, hours]) => ({
    name,
    hours: Number(hours.toFixed(2)),
  }));

  // Aggregate data by day
  const dayData = filteredLogs.reduce((acc, log) => {
    const day = new Date(log.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!acc[day]) {
      acc[day] = 0;
    }
    acc[day] += log.duration_minutes / 60;
    return acc;
  }, {} as Record<string, number>);

  const dayChartData = Object.entries(dayData)
    .map(([day, hours]) => ({
      day,
      hours: Number(hours.toFixed(2)),
    }))
    .reverse()
    .slice(0, 14); // Last 14 days

  const handleExport = () => {
    // Mock CSV export
    const csv = [
      ['Date', 'Project', 'Task', 'User', 'Duration (h)', 'Billable', 'Amount'].join(','),
      ...filteredLogs.map((log) => [
        new Date(log.start_time).toLocaleDateString(),
        log.project_name,
        log.task_name || '',
        log.user_name,
        (log.duration_minutes / 60).toFixed(2),
        log.is_billable ? 'Yes' : 'No',
        log.is_billable && log.hourly_rate ? ((log.duration_minutes / 60) * log.hourly_rate).toFixed(2) : '0',
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/time')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Time Reports</h1>
            <p className="text-muted-foreground mt-1">Analyze time tracking data and trends</p>
          </div>
        </div>
        <Button onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <Select
          value={filters.dateRange}
          onValueChange={(value) => setFilters({ ...filters, dateRange: value })}
        >
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="this_week">This Week</SelectItem>
            <SelectItem value="last_week">Last Week</SelectItem>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="this_quarter">This Quarter</SelectItem>
            <SelectItem value="this_year">This Year</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.user}
          onValueChange={(value) => setFilters({ ...filters, user: value })}
        >
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="All Users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="1">Roman Siromskyi</SelectItem>
            <SelectItem value="2">Bohdan Buryi</SelectItem>
            <SelectItem value="3">Vlad Dev</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.project}
          onValueChange={(value) => setFilters({ ...filters, project: value })}
        >
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            <SelectItem value="1">LWM PM System</SelectItem>
            <SelectItem value="2">Barcelona E-commerce</SelectItem>
            <SelectItem value="3">Fixaro Platform</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      {!isLoading && <TimeSummary logs={filteredLogs} />}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hours by Project */}
        <Card>
          <CardHeader>
            <CardTitle>Hours by Project</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-[300px] bg-muted animate-pulse rounded" />
            ) : (
              <TimeChart data={projectChartData} type="bar" dataKey="hours" xKey="name" />
            )}
          </CardContent>
        </Card>

        {/* Hours by Day */}
        <Card>
          <CardHeader>
            <CardTitle>Hours by Day (Last 14 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-[300px] bg-muted animate-pulse rounded" />
            ) : (
              <TimeChart data={dayChartData} type="line" dataKey="hours" xKey="day" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Project Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {projectChartData
              .sort((a, b) => b.hours - a.hours)
              .map((project) => {
                const totalHours = projectChartData.reduce((sum, p) => sum + p.hours, 0);
                const percentage = ((project.hours / totalHours) * 100).toFixed(1);
                return (
                  <div key={project.name}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{project.name}</span>
                      <span className="text-sm text-muted-foreground">
                        {project.hours}h ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TimeReports;
